import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, getDocs, orderBy, where, limit, startAfter, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase';
import { Timestamp } from 'firebase/firestore';
import { ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import ProgramCard from './ProgramCard';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Switch } from '@headlessui/react';

interface Program {
    id: string;
    title: string;
    subtitle?: string;
    content: string;
    contentEn?: string;
    categories: string[];
    status: 'active' | 'inactive' | 'upcoming';
    publishDate: Timestamp;
    cover?: string;
}

const categoryMapping = {
    'all': ['saturday', 'afterSchool', 'summerCamp', 'satAct', 'volunteer'],
    'saturday': 'saturday',
    'afterSchool': 'afterSchool',
    'summerCamp': 'summerCamp',
    'satAct': 'satAct',
    'volunteer': 'volunteer'
};

const PAGE_SIZE = 9; // 每页显示9个项目

const ProgramsListPage: React.FC = () => {
    const { t } = useTranslation();
    const [programs, setPrograms] = useState<Program[]>([]);
    const [lastVisible, setLastVisible] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showActive, setShowActive] = useState(true); // 修改为布尔值状态

    const fetchPrograms = async (isLoadMore = false) => {
        try {
            const programsRef = collection(db, 'programs');
            let baseConditions = [
                where('status', 'in', showActive ? ['active', 'upcoming'] : ['inactive'])
            ];

            if (selectedCategory === 'all') {
                baseConditions.push(
                    where('categories', 'array-contains-any', categoryMapping[selectedCategory])
                );
            } else {
                baseConditions.push(
                    where('categories', 'array-contains', categoryMapping[selectedCategory])
                );
            }

            let q = query(
                programsRef,
                ...baseConditions,
                orderBy('publishDate', 'desc'),
                limit(PAGE_SIZE)
            );

            if (isLoadMore && lastVisible) {
                q = query(
                    programsRef,
                    ...baseConditions,
                    orderBy('publishDate', 'desc'),
                    startAfter(lastVisible),
                    limit(PAGE_SIZE)
                );
            }

            const querySnapshot = await getDocs(q);
            const fetchedPrograms = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Program[];

            setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
            setHasMore(querySnapshot.docs.length === PAGE_SIZE);

            if (isLoadMore) {
                setPrograms(prev => [...prev, ...fetchedPrograms]);
            } else {
                setPrograms(fetchedPrograms);
            }
        } catch (error) {
            console.error('获取项目列表失败:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        setLastVisible(null);
        setPrograms([]);
        fetchPrograms();
    }, [selectedCategory, showActive]); // 更新依赖

    const handleLoadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        await fetchPrograms(true);
    };

    const getCategoryDisplayName = (category: string) => {
        const categoryMap: { [key: string]: string } = {
            'saturday': 'Saturday Program',
            'afterSchool': 'After-school Program',
            'summerCamp': 'Summer Camp',
            'satAct': 'Online Program',
            'volunteer': 'Volunteer Program'
        };
        return categoryMap[category] || category;
    };

    const getTagColor = (category: string) => {
        const colors: { [key: string]: string } = {
            'saturday': 'bg-blue-100 text-blue-800',
            'afterSchool': 'bg-yellow-100 text-yellow-800',
            'summerCamp': 'bg-green-100 text-green-800',
            'satAct': 'bg-purple-100 text-purple-800',
            'volunteer': 'bg-red-100 text-red-800'
        };
        return colors[category] || 'bg-gray-100 text-gray-800';
    };

    // 获取描述文本（从content中提取前100个字符）
    const getDescription = (content: string) => {
        return content.replace(/<[^>]+>/g, '').slice(0, 100);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white mt-32">
            {/* 英雄区域 */}
            <div className="bg-gradient-to-r from-blue-800 to-indigo-900 py-16 text-center">
                <motion.h1 
                    className="text-4xl font-bold text-white mb-4"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {t('programsList.hero.title')}
                </motion.h1>
                <motion.p 
                    className="text-xl text-blue-100 max-w-3xl mx-auto px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {t('programsList.hero.description')}
                </motion.p>
            </div>

            {/* 分类导航 */}
            <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-200">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-center space-x-1 md:space-x-4">
                        {Object.keys(t('programsList.categories', { returnObjects: true })).map((key) => (
                            <button
                                key={key}
                                onClick={() => setSelectedCategory(key)}
                                className={`py-4 px-3 md:px-6 text-sm md:text-base rounded-full transition-all duration-300 ${
                                    key === selectedCategory
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                            >
                                {t(`programsList.categories.${key}`)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* 课程列表区域 */}
            <div id="programs" className="mx-auto max-w-7xl min-h-[600px] px-4 sm:px-6 lg:px-8 py-6">
                {/* 状态切换开关 */}
                <div className="flex justify-end mb-6">
                    <Switch.Group>
                        <div className="flex items-center">
                            <Switch.Label className="mr-3 text-sm text-gray-600">
                                {t('programsList.status.showActive')}
                            </Switch.Label>
                            <Switch
                                checked={showActive}
                                onChange={setShowActive}
                                className={`${
                                    showActive ? 'bg-blue-600' : 'bg-gray-300'
                                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                            >
                                <span
                                    className={`${
                                        showActive ? 'translate-x-6' : 'translate-x-1'
                                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                />
                            </Switch>
                        </div>
                    </Switch.Group>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="text-gray-500">{t('programsList.status.loading')}</p>
                    </div>
                ) : programs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
                        <div className="text-gray-400">
                            <svg className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                        </div>
                        <p className="text-gray-500 text-lg">{t('programsList.status.empty.title')}</p>
                        <p className="text-gray-400 text-sm">{t('programsList.status.empty.subtitle')}</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {programs.map((program) => (
                                <ProgramCard
                                    key={program.id}
                                    id={program.id}
                                    type="program"
                                    title={program.title}
                                    subtitle={program.subtitle || ''}
                                    description={getDescription(program.content)}
                                    descriptionEn={program.contentEn ? getDescription(program.contentEn) : ''}
                                    image={program.cover}
                                    category={program.categories?.map(cat => getCategoryDisplayName(cat)).join(', ') || ''}
                                    status={program.status}
                                    extraInfo={{
                                        icon: 'calendar',
                                        text: program.publishDate.toDate().toLocaleDateString('zh-CN'),
                                        textEn: program.publishDate.toDate().toLocaleDateString('en-US')
                                    }}
                                />
                            ))}
                        </div>
                        
                        {hasMore && (
                            <div className="mt-12 text-center">
                                <button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-blue-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loadingMore ? (
                                        <>
                                            <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                            {t('programsList.loadMore.loading')}
                                        </>
                                    ) : (
                                        t('programsList.loadMore.button')
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ProgramsListPage; 